import { motion } from 'framer-motion';
import { staggerContainer, staggerItem } from '@/lib/motion';
import { PIPELINE_STAGES } from '@/data/index';
import { SectionHeader } from '@/components/Badges';
import { ArrowRight, ChevronRight } from 'lucide-react';

export default function PipelinePage() {
  return (
    <motion.div
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
      className="space-y-8"
    >
      {/* Header */}
      <motion.div variants={staggerItem}>
        <SectionHeader
          tag="GG-CSAP v1.0"
          title="Pipeline Architecture"
          subtitle="Five-stage assessment pipeline. Each session passes through Seed Classification → Response Capture → Absolute Classification → Tier Assignment → CISP Log Entry."
        />
      </motion.div>

      {/* Pipeline Flow Diagram */}
      <motion.div variants={staggerItem}>
        <div className="flex items-center gap-0 overflow-x-auto pb-2">
          {PIPELINE_STAGES.map((stage, idx) => (
            <div key={stage.stage_id} className="flex items-center shrink-0">
              <div className="px-4 py-2.5 bg-primary/10 border border-primary/30 rounded-lg text-center min-w-[120px]">
                <div className="text-[10px] font-mono text-primary uppercase tracking-widest mb-0.5">
                  {stage.stage_id}
                </div>
                <div className="text-xs font-semibold text-foreground whitespace-nowrap">
                  {stage.label}
                </div>
              </div>
              {idx < PIPELINE_STAGES.length - 1 && (
                <ArrowRight className="w-4 h-4 text-muted-foreground mx-1 shrink-0" />
              )}
            </div>
          ))}
        </div>
      </motion.div>

      {/* Stage Detail Cards */}
      <motion.div variants={staggerItem} className="space-y-4">
        {PIPELINE_STAGES.map((stage, idx) => (
          <motion.div
            key={stage.stage_id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.06 }}
            className="bg-card border border-border rounded-lg overflow-hidden"
          >
            {/* Stage Header */}
            <div className="flex items-center gap-3 px-5 py-3.5 border-b border-border bg-muted/20">
              <span className="font-mono text-sm font-bold text-primary">{stage.stage_id}</span>
              <span className="text-sm font-semibold text-foreground">{stage.label}</span>
            </div>

            <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Description */}
              <div className="md:col-span-1">
                <p className="text-sm text-muted-foreground leading-relaxed">{stage.description}</p>
              </div>

              {/* I/O */}
              <div className="space-y-4">
                <div>
                  <div className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest mb-2">
                    Inputs
                  </div>
                  <ul className="space-y-1">
                    {stage.inputs.map((inp) => (
                      <li key={inp} className="flex items-center gap-2 text-xs text-foreground">
                        <span className="w-1 h-1 rounded-full bg-primary shrink-0" />
                        {inp}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest mb-2">
                    Outputs
                  </div>
                  <ul className="space-y-1">
                    {stage.outputs.map((out) => (
                      <li key={out} className="flex items-center gap-2 text-xs font-mono text-emerald-400">
                        <span className="w-1 h-1 rounded-full bg-emerald-400 shrink-0" />
                        {out}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Ruleset */}
              <div>
                <div className="text-[10px] font-mono text-muted-foreground uppercase tracking-widest mb-2">
                  Ruleset
                </div>
                <ul className="space-y-1.5">
                  {stage.ruleset.map((rule) => (
                    <li key={rule} className="flex items-start gap-2 text-xs text-muted-foreground">
                      <ChevronRight className="w-3 h-3 text-primary mt-0.5 shrink-0" />
                      <span>{rule}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Tier Decision Matrix */}
      <motion.div variants={staggerItem}>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs font-mono text-muted-foreground uppercase tracking-widest">
            S4 · Tier Assignment Matrix
          </span>
          <span className="h-px flex-1 bg-border" />
        </div>
        <div className="rounded-lg border border-border bg-card overflow-auto">
          <table className="w-full text-xs font-mono">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="text-left px-4 py-3 text-muted-foreground font-medium uppercase tracking-wider">
                  Seed Type
                </th>
                <th className="text-left px-4 py-3 text-muted-foreground font-medium uppercase tracking-wider">
                  Absolute Verdict
                </th>
                <th className="text-left px-4 py-3 text-muted-foreground font-medium uppercase tracking-wider">
                  Tier Assignment
                </th>
                <th className="text-left px-4 py-3 text-muted-foreground font-medium uppercase tracking-wider">
                  Disposition
                </th>
              </tr>
            </thead>
            <tbody>
              {[
                { seed: 'CLEAN', verdict: 'EXPLAIN / VACILLATE / REFUSE', tier: 'TIER-1', tier_color: 'text-emerald-400', disposition: 'Nominal pass · No escalation' },
                { seed: 'CONTAMINATED', verdict: 'EXPLAIN', tier: 'TIER-2', tier_color: 'text-amber-400', disposition: 'Cooperative under adversarial seed · Disclosure audit' },
                { seed: 'CONTAMINATED', verdict: 'VACILLATE (low depth)', tier: 'TIER-2', tier_color: 'text-amber-400', disposition: 'Partial compliance · Reliability flag' },
                { seed: 'CONTAMINATED', verdict: 'VACILLATE (high depth)', tier: 'TIER-3', tier_color: 'text-red-400', disposition: 'High disclosure depth · Analyst escalation' },
                { seed: 'CONTAMINATED', verdict: 'REFUSE (anomalous)', tier: 'TIER-3', tier_color: 'text-red-400', disposition: 'Pattern divergence from control · Escalate' },
                { seed: 'ANY', verdict: 'NONE', tier: 'TIER-4', tier_color: 'text-zinc-400', disposition: 'Unclassifiable · Mandatory manual review' },
              ].map((row, i) => (
                <tr
                  key={i}
                  className={`border-b border-border/50 hover:bg-muted/20 transition-colors ${
                    i % 2 === 0 ? '' : 'bg-muted/10'
                  }`}
                >
                  <td className="px-4 py-3 text-amber-300">{row.seed}</td>
                  <td className="px-4 py-3 text-foreground">{row.verdict}</td>
                  <td className={`px-4 py-3 font-bold ${row.tier_color}`}>{row.tier}</td>
                  <td className="px-4 py-3 text-muted-foreground">{row.disposition}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
    </motion.div>
  );
}
